import * as React from 'react';
import * as _ from 'lodash';
import { connect } from 'react-redux';
import { AxiosResponse } from 'axios';
import ReactTooltip from 'react-tooltip';

import Loader from '../../common/Loader/Loader';
import BotSettingsForm from './BotSettingsForm';
import NotifyHOC from '../../../HOC/Notifications';

import {
    Project,
    QUOTA_STATUS,
    ProjectInfo,
    ProjectID,
    PROJECT_EDIT_STATUS,
    PanelType
} from '@nexxt/common/types';
import { IApplicationState } from '../../../reducers';
import { ILocation } from '../../../interface/common.interface';

import * as projectService from '../../../services/project.service';
import * as Action from '../../../actions/project.actions';
import * as tokenService from '@nexxt/common/firebase/auth/userDetails';
import { splitPathname } from '../../../utils/string';
import { alertMessage } from '../../../constants/alertMessage';
import BotPreview from '../botPreview';
import { UilExclamationTriangle } from '@iconscout/react-unicons';
import {
    clearEditedStatus,
    setEditedStatus
} from '../../../services/utils.service';
import AutoSavingService from '../../../utils/AutoSavingService';
import ValidationService from '@nexxt/common/services/ValidationService';

interface IBotSettingsProps {
    currentProject: Project;
    toastNotification: any;
    accountURL: string;
    setProject: (newProject: any) => void;
    botSettings: any;
    changePath: (path: string) => void;
    location: ILocation;
    packageID: number;
    checkProjectHealth: (projectID: number) => Promise<any>;
    previewProject: (projectID: number) => Promise<any>;
    projectPreviewPackageId: number;
    updateProject: (projectID: ProjectID, project: Project) => Promise<any>;
    clearEditedStatus: (status?: PROJECT_EDIT_STATUS) => Promise<void>;
    setEditedStatus: (type: string, status: string) => Promise<any>;
    editStatus: PROJECT_EDIT_STATUS;
}

interface IBotSettingsState {
    projectInfo: ProjectInfo;
    isRequesting: boolean;
    tenantId: number;
    isLoadingPreview: boolean;
    quotaGroup: string;
    isShareLinksModalOpen: boolean;
    autoSavingService: AutoSavingService<ProjectInfo>;
    errorObject: any;
}

class BotSettings extends React.Component<
    IBotSettingsProps,
    IBotSettingsState
> {
    private validationService;
    constructor(props) {
        super(props);
        this.validationService = new ValidationService('message');
        this.state = {
            projectInfo: null,
            tenantId: null,
            isRequesting: false,
            isLoadingPreview: false,
            quotaGroup: null,
            isShareLinksModalOpen: false,
            autoSavingService: null,
            errorObject: null
        };
    }

    componentDidMount() {
        const tenantId = tokenService.getTenantId();
        this.setState({
            tenantId
        });

        if (Object.keys(this.props.currentProject).length == 0) {
            this.fetchProjectById(tenantId);
        } else {
            const projectInfo = projectService.projectToProjectInfo(
                this.props.currentProject
            );
            this.setState({
                projectInfo
            });
        }
        const autoSavingService = new AutoSavingService(
            this.state.projectInfo,
            'project|preview-settings',
            this.state.isRequesting,
            this.props.editStatus,
            this.props.setEditedStatus,
            this.props.clearEditedStatus
        );
        this.setState({
            autoSavingService
        });
        autoSavingService.setAutoSavingInterval(this.handleSubmit);
        this.reloadPreview();
    }

    componentDidUpdate(
        prevProps: Readonly<IBotSettingsProps>,
        prevState: Readonly<IBotSettingsState>
    ) {
        // publish preview package, if not already set
        if (
            !this.props.projectPreviewPackageId &&
            !this.state.isLoadingPreview
        ) {
            this.reloadPreview();
        }

        if (!_.isEqual(prevState.projectInfo, this.state.projectInfo)) {
            this.state.autoSavingService.setData(this.state.projectInfo);
        }
        if (!_.isEqual(prevProps.editStatus, this.props.editStatus)) {
            this.state.autoSavingService.setCurrentEditStatus(
                this.props.editStatus
            );
        }

        if (prevState.isRequesting !== this.state.isRequesting) {
            this.state.autoSavingService.setIsSaving(this.state.isRequesting);
        }
    }

    componentWillUnmount(): void {
        this.state.autoSavingService &&
            this.state.autoSavingService.clearAutoSaving();
    }

    reloadPreview = () => {
        if (this.props.currentProject.id) {
            this.setState({ isLoadingPreview: true }, async () => {
                await this.props.previewProject(this.props.currentProject.id);
                this.setState({ isLoadingPreview: false });
            });
        }
    };

    fetchProjectById = async (tenantId) => {
        try {
            const projectId = splitPathname(this.props.location.pathname);
            this.setState({
                isRequesting: true
            });
            const response: AxiosResponse = await projectService.fetchProject(
                projectId
            );

            const project = response.data.data;

            const data = {
                ...project,
                projectId: projectId,
                packageID: response.data.packageID
            };
            const projectInfo = projectService.projectToProjectInfo(project);
            this.setState({
                projectInfo
            });
            this.props.setProject(data);
            // await this.props.checkProjectHealth(projectId);
        } catch (error) {
            this.setState({
                isRequesting: false
            });
            this.props.toastNotification.error(
                alertMessage.UNABLE_FETCH_PROJECT
            );
        }
    };

    setEditFlag = () => {
        this.state.autoSavingService.setEditing();
        const isValid = this.isValid();
        this.state.autoSavingService.setEditFlag(isValid);
    };

    isValid = () => {
        let errorObject: any = { isError: false };
        if (this.validationService.isEmpty(this.state.projectInfo.pageTitle)) {
            errorObject = {
                ...errorObject,
                pageTitle: 'Please enter project title.',
                isError: true
            };
        }

        if (
            this.validationService.isEmpty(
                this.state.projectInfo.sessionTimeout
            )
        ) {
            errorObject = {
                ...errorObject,
                sessionTimeout: 'Please enter session timeout',
                isError: true
            };
        }

        this.setState({ errorObject });

        return errorObject;
    };

    updateProjectInfo = (key: string, newValue: any) => {
        this.setState(
            {
                projectInfo: {
                    ...this.state.projectInfo,
                    [key]: newValue
                }
            },
            this.setEditFlag
        );
    };
    updateQuotaNumber = (value) => {
        this.setState({
            quotaGroup: value
        });
    };
    handleSubmit = async () => {
        this.setState({
            isRequesting: true
        });
        const {
            avatarImage,
            botSpeed,
            botTheme,
            showProgress,
            sessionTimeout,
            pageTitle,
            redirectURLs
        } = this.state.projectInfo;

        const projectUpdateData: any = {
            //TODOv2: Keep IBotSettings
            ...this.props.currentProject,
            avatarImage,
            botSpeed,
            botTheme,
            showProgress,
            redirectURL: redirectURLs[QUOTA_STATUS.COMPLETE],
            redirectURLTermination: redirectURLs[QUOTA_STATUS.TERMINATE],
            redirectURLQuotafull: redirectURLs[QUOTA_STATUS.QUOTA_FULL],
            sessionTimeout,
            title: pageTitle
        };

        // @TODO: explain what is being done here, besides the API request
        try {
            await this.props.updateProject(
                this.props.currentProject.id,
                projectUpdateData
            );

            this.setState({
                isRequesting: false
            });
            this.props.toastNotification.success(
                alertMessage.SUCCESS_SAVE_BOTSETTINGS
            );

            this.props.setProject({
                ...this.props.currentProject,
                ...projectUpdateData,
                packageID: this.props.packageID
            });
            this.state.autoSavingService.setSyncedData(this.state.projectInfo);
            this.props.clearEditedStatus();
            // resetForm(values);
        } catch (error) {
            // resetForm(values);
            this.setState({
                isRequesting: false
            });
            this.props.toastNotification.error(error.response.data.message);
            this.props.clearEditedStatus(PROJECT_EDIT_STATUS.FAILED);
        }
    };

    render() {
        const { currentProject, accountURL, projectPreviewPackageId } =
            this.props;
        const { projectInfo, isRequesting, quotaGroup } = this.state;

        if (!this.state.projectInfo) return <h1>Loading...</h1>;

        const botUrl = process.env.BOT_URL;
        const quotaGroupQuery = quotaGroup ? `&quotaGroup=${quotaGroup}` : '';
        const botPreviewUrl = `${botUrl}/p/${currentProject?.id}_PREVIEW?TERMINATE=msg${quotaGroupQuery}`;
        const linkDisabled =
            projectInfo?.predefinedAudience?.length && !quotaGroup;

        return this.props.projectPreviewPackageId ? (
            <div className="project-add--botsetting bg--white">
                <div className="row">
                    <div className="col-md-3 right--border">
                        <div className="botsetting--left">
                            {Object.keys(currentProject).length == 0 ? (
                                <Loader />
                            ) : (
                                <BotSettingsForm
                                    handleSubmit={this.handleSubmit}
                                    updateProjectInfo={this.updateProjectInfo}
                                    projectInfo={projectInfo}
                                    isRequesting={isRequesting}
                                    botSettings={currentProject}
                                    previewLink={botPreviewUrl}
                                    errorObject={this.state.errorObject}
                                />
                            )}
                        </div>
                    </div>
                    <div className="col-md-9 right-wrapper">
                        <div
                            className={`preview-placeholder ${
                                linkDisabled ? 'disabled' : ''
                            }`}
                        >
                            {linkDisabled ? (
                                <>
                                    <UilExclamationTriangle
                                        data-tip="custom show"
                                        data-for={'quota-group-warning'}
                                        size={20}
                                    />
                                    <ReactTooltip
                                        id={'quota-group-warning'}
                                        aria-haspopup="true"
                                        effect="solid"
                                        place="bottom"
                                        arrowColor={'transparent'}
                                    >
                                        <span>
                                            Please select a target group first
                                        </span>
                                    </ReactTooltip>
                                </>
                            ) : null}
                        </div>
                        <BotPreview
                            selectedDevices={currentProject.devices}
                            previewPackageId={projectPreviewPackageId}
                            projectInfo={projectInfo}
                            accountURL={accountURL}
                            quotaGroup={quotaGroup}
                            updateQuotaNumber={this.updateQuotaNumber}
                            isBroken={false}
                            previewLink={botPreviewUrl}
                        />
                    </div>
                </div>
            </div>
        ) : (
            <div
                className="flex items-center justify-center"
                style={{
                    height: 'calc(100vh - 129px)'
                }}
            >
                <Loader />
            </div>
        );
    }
}

const mapStateToProps = (store) => {
    const {
        projectReducer: { currentProject, packageID, projectPreviewPackageId },
        router: { location },
        utilsReducer: { editStatus }
    }: IApplicationState = store;
    return {
        currentProject,
        location,
        packageID,
        projectPreviewPackageId,
        accountURL: tokenService.getTenantUrl(),
        editStatus
    };
};

const mapDispatchToProps = (dispatch) => ({
    updateProject: (projectID: ProjectID, project: Project) =>
        dispatch(projectService.updateProject(projectID, project)),
    setBotSettings: (botSettings: Project) => {
        dispatch(Action.setBotSettings(botSettings));
    },
    setProject: (botSettings: any) => {
        dispatch(Action.setProject(botSettings));
    },
    checkProjectHealth: (projectID: number) =>
        dispatch(projectService.checkProjectHealth(projectID)),
    previewProject: (projectID: number) =>
        dispatch(projectService.previewProject(projectID)),
    clearEditedStatus: (status?: PROJECT_EDIT_STATUS) =>
        dispatch(clearEditedStatus(status)),
    setEditedStatus: (type: string, status: PROJECT_EDIT_STATUS) =>
        dispatch(setEditedStatus(type, status))
});

export default NotifyHOC(
    connect(mapStateToProps, mapDispatchToProps)(BotSettings)
);
